/* eslint-disable */
// features/cx/outstanding/hooks.ts

import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/hooks/use-toast";
import { useCallback, useState } from "react";
import { outstandingApi } from "./api";

import { queryClient } from "@shared/api/queries/queryClient";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  CreatePaymentAgreementPayload,
  OutstandingBillsFilters,
  PaymentAgreementFilters,
} from "./types";

// ================================
// OUTSTANDING BILLS HOOKS
// ================================

/**
 * Get outstanding bills list with pagination and search
 */
export const useOutstandingBills = (filters: OutstandingBillsFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.list(filters),
    () => outstandingApi.getOutstandingBills(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useOutstandingBillsExport = (filters: OutstandingBillsFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.exportList(filters),
    async () => {
      setIsExporting(true);
      try {
        const blob = await outstandingApi.exportOutstandingBills(filters);

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export outstanding bills: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    },
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get outstanding summary statistics
 */
export const useOutstandingSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
    () => outstandingApi.getOutstandingSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

/**
 * Get account details by ID
 */
export const useAccountDetails = (params: {
  remoteUtilityId: any;
  consumerNumber: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.accountDetails(params.consumerNumber),
    () => outstandingApi.getAccountDetails(params),
    {
      enabled: !!params.consumerNumber && !!params.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

// ================================
// PAYMENT AGREEMENT HOOKS
// ================================

/**
 * Get payment agreement detail
 */
export const usePaymentAgreementDetail = (params: {
  remoteUtilityId: number;
  remoteBillId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(
      params.remoteUtilityId,
      params.remoteBillId,
    ),
    () => outstandingApi.getPaymentAgreementDetail(params),
    {
      enabled: !!params.remoteUtilityId && !!params.remoteBillId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

/**
 * Get payment agreements list
 */
export const usePaymentAgreements = (filters: PaymentAgreementFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.paymentAgreements(
      filters.remoteUtilityId,
      filters.remoteBillId,
    ),
    () => outstandingApi.getPaymentAgreements(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};
/**
 * Get payment agreement list (paginated)
 */

/**
 * Get payment agreement list (paginated)
 */
export const usePaymentAgreementList = (filters: PaymentAgreementFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.paymentAgreementList(filters),
    () => outstandingApi.getPaymentAgreementList(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

/**
 * Create payment agreement
 */
export const useCreatePaymentAgreement = (
  remoteUtilityId: number,
  remoteBillId?: number,
) => {
  return useSmartMutation(
    (payload: any) => outstandingApi.createPaymentAgreement(payload),
    {
      onSuccess: () => {
        toast({
          title: "Payment agreement created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "outstanding", "paymentAgreementList"],
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreements(
            remoteUtilityId,
            remoteBillId,
          ),
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(
              remoteUtilityId,
              remoteBillId,
            ),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.list(),
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "outstanding", "paymentAgreementList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to create payment agreement!",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Update payment agreement
 */
export const useUpdatePaymentAgreement = (
  remoteUtilityId: number,
  remoteBillId: number,
  agreementId: number,
) => {
  return useSmartMutation(
    (payload: Partial<CreatePaymentAgreementPayload>) =>
      outstandingApi.updatePaymentAgreement(agreementId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.outstanding.paymentAgreements(
          remoteUtilityId,
          remoteBillId,
        ),
        QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(
          remoteUtilityId,
          remoteBillId,
        ),
        QueryKeyFactory.module.cx.outstanding.list(),
        QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({
          title: "Payment agreement updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update payment agreement!",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Delete payment agreement
 */
export const useDeletePaymentAgreement = (
  remoteUtilityId: number,
  remoteBillId: number,
) => {
  return useSmartMutation(
    (agreementId: number) =>
      outstandingApi.deletePaymentAgreement({ agreementId }),
    {
      onSuccess: () => {
        toast({
          title: "Payment agreement deleted successfully!",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.paymentAgreements(
            remoteUtilityId,
            remoteBillId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.outstanding.paymentAgreementDetail(
              remoteUtilityId,
              remoteBillId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.cx.outstanding.list(),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.cx.outstanding.summary(remoteUtilityId),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to delete payment agreement!",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Send reminder
 */
export const useSendReminder = () => {
  return useSmartMutation(
    (payload: any) => outstandingApi.sendReminder(payload),
    {
      onSuccess: () => {
        toast({
          title: "Reminder sent successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to send reminder!",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Update response time
 */
export const useUpdateResponseTime = () => {
  return useSmartMutation(
    (payload: any) => outstandingApi.updateResponseTime(payload),
    {
      onSuccess: () => {
        toast({
          title: "Response time updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: error?.message || "Failed to update response time!",
          variant: "destructive",
        });
      },
    },
  );
};

/**
 * Custom hook for payment agreement filters and pagination
 */
export const usePaymentAgreementFilters = (
  initialFilters: PaymentAgreementFilters,
) => {
  const [filters, setFilters] = useState<PaymentAgreementFilters>({
    page: 1,
    limit: 50,
    ...initialFilters,
  });

  const updateFilters = useCallback(
    (newFilters: Partial<PaymentAgreementFilters>) => {
      setFilters((prev) => ({
        ...prev,
        ...newFilters,
        page: newFilters.page || 1,
      }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: 50,
      ...initialFilters,
    });
  }, [initialFilters]);

  const nextPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: (prev.page || 1) + 1,
    }));
  }, []);

  const prevPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max((prev.page || 1) - 1, 1),
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.max(page, 1),
    }));
  }, []);

  const queryResult = usePaymentAgreements(filters);

  return {
    filters,
    updateFilters,
    resetFilters,
    nextPage,
    prevPage,
    setPage,
    ...queryResult,
  };
};

export const useInstallmentFrequency = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.installmentFrequency(),
    () => outstandingApi.getInstallmentFrequency(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useWeekdayOptions = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.weekdayOptions(),
    () => outstandingApi.getWeekdayOptions(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const usePreferredDayOfMonth = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.preferredDayOfMonth(),
    () => outstandingApi.getPreferredDayOfMonth(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useChannelChoices = (context: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.channelChoices(
      remoteUtilityId?.toString() || "",
      context,
    ),
    () =>
      outstandingApi.getChannelChoices({
        remoteUtilityId: remoteUtilityId?.toString() || "",
        context,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useNoticeTemplates = (channelType: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.noticeTemplates(
      remoteUtilityId?.toString() || "",
      channelType,
    ),
    () =>
      outstandingApi.getTemplates({
        remoteUtilityId: remoteUtilityId?.toString() || "",
        channelType,
        isActive: "1",
      }),
    {
      enabled: !!remoteUtilityId && !!channelType,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useBulkCommunicationStep1 = (filters: {
  remoteUtilityId: string | number;
  risk?: string;
  agingBucket?: string;
  status?: string;
  category?: string;
  subCategory?: string;
  premise?: string;
  page?: number;
  limit?: number;
  searchData?: string;
  consumerIds?: (string | number)[];
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.bulkCommunicationStep1(filters),
    () => outstandingApi.getBulkCommunicationStep1(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useSaveBulkCommunicationStep1 = () => {
  return useSmartMutation(
    (payload: {
      consumerIds: number[];
      remoteUtilityId: number | string;
      is_select_all?: boolean;
    }) => outstandingApi.saveBulkCommunicationStep1(payload),
    {
      onSuccess: () => {
        toast({
          title: "Selected accounts saved successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: error?.message || "Failed to save selected accounts!",
          variant: "destructive",
        });
      },
    },
  );
};

export const useSaveBulkCommunicationStep3 = () => {
  return useSmartMutation(
    (formData: FormData) => outstandingApi.saveBulkCommunicationStep3(formData),
    {
      onSuccess: () => {
        toast({
          title: "Bulk communication batch dispatched successfully!",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "outstanding", "bulkCommunicationAuditSummary"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "outstanding", "bulkCommunicationAuditBatches"],
        });
      },
      onError: (error: any) => {
        toast({
          title:
            error?.message || "Failed to dispatch bulk communication batch!",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUserNames = (params: { remoteUtilityId: string | number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.userNames(params),
    () => outstandingApi.getUserNames({ ...params }),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useBulkCommunicationAuditSummary = (
  remoteUtilityId: string | number,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.bulkCommunicationAuditSummary(
      remoteUtilityId,
    ),
    () => outstandingApi.getBulkCommunicationAuditSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useBulkCommunicationAuditBatches = (filters: {
  remoteUtilityId: string | number;
  page?: number;
  limit?: number;
  searchData?: string;
  type?: string;
  template?: string;
  status?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.bulkCommunicationAuditBatches(
      filters,
    ),
    () => outstandingApi.getBulkCommunicationAuditBatches(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    },
  );
};

export const useBulkCommunicationResult = (params: {
  id: string | number;
  remoteUtilityId: string | number;
  page?: number;
  limit?: number;
}) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.cx.outstanding.bulkCommunicationResult(
        params.id,
        params.remoteUtilityId,
      ),
      params.page,
      params.limit,
    ],
    () => outstandingApi.getBulkCommunicationResult(params),
    {
      enabled: !!params.id && !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useGetCategory = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(QueryKeyFactory.module.cx.outstanding.category(), () =>
    outstandingApi.getCategory(utilityId),
  );
};

export const useGetSubCategory = (code_list: any[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.subCategory(code_list),
    () => outstandingApi.getSubCategory(utilityId, code_list),
    {
      enabled: !!code_list?.length && !!utilityId,
    },
  );
};

export const useGetPremise = (params: {
  remote_utility_id: any;
  config_level: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.outstanding.premise(params),
    () => outstandingApi.getPremise(params),
    {
      enabled: !!params.remote_utility_id,
    },
  );
};

export const useDownloadBulkCommunicationReport = () => {
  return useSmartMutation(
    ({
      batchId,
      remoteUtilityId,
    }: {
      batchId: string | number;
      remoteUtilityId: string | number;
    }) =>
      outstandingApi.downloadBulkCommunicationReport(batchId, remoteUtilityId),
    {
      onError: (error: any) => {
        toast({
          title: error?.message || "Failed to download delivery report",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDownloadBulkCommunicationErrors = () => {
  return useSmartMutation(
    ({
      batchId,
      remoteUtilityId,
    }: {
      batchId: string | number;
      remoteUtilityId: string | number;
    }) =>
      outstandingApi.downloadBulkCommunicationErrors(batchId, remoteUtilityId),
    {
      onError: (error: any) => {
        toast({
          title: error?.message || "Failed to download error report",
          variant: "destructive",
        });
      },
    },
  );
};
